import { useState, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HomeworkAPI } from "@/lib/sprint8-api";
import { ClassesAPI, SectionsAPI, InstitutionsAPI } from "@/lib/sprint2-api";
import { SubjectsAPI } from "@/lib/sprint2-api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import {
  Loader2, Plus, ClipboardList, CalendarDays, AlertTriangle, CheckCircle2,
  Paperclip, Link as LinkIcon, Trash2, Pencil, X, Search, Users, BookOpen,
  Upload, ExternalLink, LayoutGrid, Calendar as CalendarIcon, ChevronLeft, ChevronRight,
} from "lucide-react";

const TEACHER_ROLES = [
  "super_admin", "tenant_admin", "inst_admin", "campus_admin",
  "principal", "hod", "coordinator", "teacher", "class_teacher",
];

// Days until a due-date. Positive = future, negative = overdue, 0 = today.
function daysUntil(dueDate) {
  const t = new Date(dueDate + "T23:59:59");
  const now = new Date();
  return Math.ceil((t - now) / (1000 * 60 * 60 * 24));
}

function DueBadge({ dueDate }) {
  const d = daysUntil(dueDate);
  let cls = "bg-emerald-100 text-emerald-700 border-emerald-200";
  let label = `Due in ${d} day${d === 1 ? "" : "s"}`;
  if (d < 0) { cls = "bg-red-100 text-red-700 border-red-200"; label = `Overdue · ${Math.abs(d)}d`; }
  else if (d === 0) { cls = "bg-red-100 text-red-700 border-red-200"; label = "Due today"; }
  else if (d <= 3) { cls = "bg-amber-100 text-amber-700 border-amber-200"; }
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${cls}`} data-testid={`hw-due-${d < 0 ? "overdue" : d === 0 ? "today" : d <= 3 ? "soon" : "later"}`}>
      {label}
    </span>
  );
}

export default function Homework() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const roles = user?.roles || [];
  const isTeacher = roles.some((r) => TEACHER_ROLES.includes(r));
  const isStudent = roles.includes("student") && !isTeacher;
  const isCT = roles.includes("class_teacher");

  const [view, setView] = useState("list"); // list | calendar
  const [search, setSearch] = useState("");
  const [classFilter, setClassFilter] = useState("all");
  const [sectionFilter, setSectionFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all"); // all | upcoming | overdue | done
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null); // homework obj
  const [showEdit, setShowEdit] = useState(null); // homework obj

  const listQ = useQuery({
    queryKey: ["homework-list"],
    queryFn: () => HomeworkAPI.list(),
  });
  const items = listQ.data || [];

  // Look up class/section/subject metadata so cards can show human-readable chips.
  const instQ = useQuery({ queryKey: ["hw-insts"], queryFn: () => InstitutionsAPI.list() });
  const instId = user?.institution_id || instQ.data?.[0]?.id;
  const classesQ = useQuery({
    queryKey: ["hw-classes", instId],
    queryFn: () => ClassesAPI.list({ institution_id: instId }),
    enabled: !!instId,
  });
  const sectionsQ = useQuery({
    queryKey: ["hw-sections", classFilter],
    queryFn: () => SectionsAPI.list({ class_id: classFilter }),
    enabled: classFilter !== "all",
  });
  const subjectsQ = useQuery({
    queryKey: ["hw-subjects", classFilter],
    queryFn: () => SubjectsAPI.list({ class_id: classFilter }),
    enabled: classFilter !== "all",
  });
  const myClassesQ = useQuery({
    queryKey: ["hw-my-classes"],
    queryFn: () => SectionsAPI.myClasses(),
    enabled: isCT,
  });

  const classNameById = useMemo(() => {
    const m = new Map();
    (classesQ.data || []).forEach((c) => m.set(c.id, c.name));
    return m;
  }, [classesQ.data]);

  // Auto-scope for Class Teachers on first load: preselect their first CT section's class.
  useEffect(() => {
    if (isCT && myClassesQ.data?.length && classFilter === "all") {
      setClassFilter(myClassesQ.data[0].class_id);
      setSectionFilter(myClassesQ.data[0].id);
    }
  }, [isCT, myClassesQ.data, classFilter]);

  // Apply filters + search client-side.
  const filtered = useMemo(() => {
    return items.filter((h) => {
      if (classFilter !== "all" && h.class_id !== classFilter) return false;
      if (sectionFilter !== "all" && h.section_id !== sectionFilter) return false;
      if (subjectFilter !== "all" && h.subject_id !== subjectFilter) return false;
      const d = daysUntil(h.due_date);
      if (statusFilter === "upcoming" && d < 0) return false;
      if (statusFilter === "overdue" && d >= 0) return false;
      if (statusFilter === "done" && h.my_status !== "done") return false;
      if (search) {
        const q = search.toLowerCase();
        if (!h.title.toLowerCase().includes(q) && !h.body.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [items, classFilter, sectionFilter, subjectFilter, statusFilter, search]);

  // KPIs
  const kpis = useMemo(() => {
    const total = items.length;
    const nowMs = Date.now();
    const weekMs = 7 * 24 * 60 * 60 * 1000;
    const dueThisWeek = items.filter((h) => {
      const t = new Date(h.due_date + "T23:59:59").getTime();
      return t >= nowMs && t <= nowMs + weekMs;
    }).length;
    const overdue = items.filter((h) => daysUntil(h.due_date) < 0).length;
    const submissions = items.reduce((s, h) => s + (h.submission_count || 0), 0);
    return { total, dueThisWeek, overdue, submissions };
  }, [items]);

  return (
    <AppShell>
      <div className="space-y-5" data-testid="hw-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-black tracking-tight text-3xl">Homework</h1>
            <p className="text-sm text-muted-foreground max-w-2xl mt-1">
              Assign work to a class or section, share attachments and links, and track submissions in one place.
              {isCT && myClassesQ.data?.length && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold">
                  <Users className="h-3 w-3" /> Class Teacher of {myClassesQ.data.length} section{myClassesQ.data.length === 1 ? "" : "s"}
                </span>
              )}
            </p>
          </div>
          {isTeacher && (
            <Button onClick={() => setShowCreate(true)} data-testid="hw-create-btn">
              <Plus className="h-4 w-4 mr-1.5" /> New Homework
            </Button>
          )}
        </div>

        {/* KPI tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hw-kpis">
          <KpiTile icon={<ClipboardList className="h-4 w-4" />} label="Total assignments" value={kpis.total} tint="slate" testid="hw-kpi-total" />
          <KpiTile icon={<CalendarDays className="h-4 w-4" />} label="Due this week" value={kpis.dueThisWeek} tint="amber" testid="hw-kpi-week" />
          <KpiTile icon={<AlertTriangle className="h-4 w-4" />} label="Overdue" value={kpis.overdue} tint="red" testid="hw-kpi-overdue" />
          <KpiTile icon={<CheckCircle2 className="h-4 w-4" />} label={isStudent ? "My submissions" : "Submissions received"} value={isStudent ? items.filter((h) => h.my_status === "done").length : kpis.submissions} tint="emerald" testid="hw-kpi-subs" />
        </div>

        {/* Filter bar */}
        <div className="bg-card border border-border rounded-lg p-3 flex flex-wrap gap-2 items-center" data-testid="hw-filters">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search title or description…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-9" data-testid="hw-search" />
          </div>
          <Select value={classFilter} onValueChange={(v) => { setClassFilter(v); setSectionFilter("all"); setSubjectFilter("all"); }}>
            <SelectTrigger className="w-[150px] h-9" data-testid="hw-filter-class"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {(classesQ.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={sectionFilter} onValueChange={setSectionFilter} disabled={classFilter === "all"}>
            <SelectTrigger className="w-[130px] h-9" data-testid="hw-filter-section"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sections</SelectItem>
              {(sectionsQ.data || []).map((s) => <SelectItem key={s.id} value={s.id}>Sec {s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={subjectFilter} onValueChange={setSubjectFilter} disabled={classFilter === "all"}>
            <SelectTrigger className="w-[150px] h-9" data-testid="hw-filter-subject"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All subjects</SelectItem>
              {(subjectsQ.data || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Tabs value={statusFilter} onValueChange={setStatusFilter} className="h-9">
            <TabsList className="h-9">
              <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
              <TabsTrigger value="upcoming" className="text-xs" data-testid="hw-tab-upcoming">Upcoming</TabsTrigger>
              <TabsTrigger value="overdue" className="text-xs" data-testid="hw-tab-overdue">Overdue</TabsTrigger>
              {isStudent && <TabsTrigger value="done" className="text-xs">Done</TabsTrigger>}
            </TabsList>
          </Tabs>
          <div className="ml-auto flex items-center gap-1 border border-border rounded-md p-0.5">
            <Button size="sm" variant={view === "list" ? "default" : "ghost"} onClick={() => setView("list")} className="h-8 px-2" data-testid="hw-view-list">
              <LayoutGrid className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant={view === "calendar" ? "default" : "ghost"} onClick={() => setView("calendar")} className="h-8 px-2" data-testid="hw-view-cal">
              <CalendarIcon className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Content */}
        {listQ.isLoading ? (
          <div className="text-center py-16"><Loader2 className="h-6 w-6 animate-spin inline text-muted-foreground" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState hasFilters={search || classFilter !== "all" || subjectFilter !== "all" || statusFilter !== "all"} />
        ) : view === "list" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="hw-list">
            {filtered.map((h) => (
              <HomeworkCard
                key={h.id}
                hw={h}
                className={classNameById.get(h.class_id)}
                isStudent={isStudent}
                isTeacher={isTeacher}
                userId={user?.id}
                onOpen={() => setShowDetail(h)}
                onEdit={() => setShowEdit(h)}
                onDeleted={() => qc.invalidateQueries({ queryKey: ["homework-list"] })}
                onMarkChanged={() => qc.invalidateQueries({ queryKey: ["homework-list"] })}
              />
            ))}
          </div>
        ) : (
          <WeeklyCalendar items={filtered} onSelect={setShowDetail} />
        )}
      </div>

      {showCreate && (
        <CreateEditDialog
          mode="create"
          instId={instId}
          classes={classesQ.data || []}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ["homework-list"] }); }}
        />
      )}
      {showEdit && (
        <CreateEditDialog
          mode="edit"
          hw={showEdit}
          instId={instId}
          classes={classesQ.data || []}
          onClose={() => setShowEdit(null)}
          onSaved={() => { setShowEdit(null); qc.invalidateQueries({ queryKey: ["homework-list"] }); }}
        />
      )}
      {showDetail && (
        <DetailDialog
          hw={showDetail}
          isTeacher={isTeacher}
          className={classNameById.get(showDetail.class_id)}
          onClose={() => setShowDetail(null)}
        />
      )}
    </AppShell>
  );
}

function KpiTile({ icon, label, value, tint, testid }) {
  const tints = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    red: "border-red-200 bg-red-50 text-red-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
  return (
    <div className={`rounded-lg border p-4 ${tints[tint] || tints.slate}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5 opacity-80">{icon}{label}</div>
      <div className="font-display font-black text-3xl mt-1 tracking-tight">{value}</div>
    </div>
  );
}

function HomeworkCard({ hw, className, isStudent, isTeacher, userId, onOpen, onEdit, onDeleted, onMarkChanged }) {
  const [busy, setBusy] = useState(false);
  const isAuthor = String(hw.created_by) === String(userId);
  const attCount = (hw.attachment_urls || []).length;

  const remove = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${hw.title}"?`)) return;
    setBusy(true);
    try { await HomeworkAPI.remove(hw.id); toast.success("Homework deleted"); onDeleted?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed"); }
    finally { setBusy(false); }
  };

  const toggleDone = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      if (hw.my_status === "done") { await HomeworkAPI.unmarkDone(hw.id); toast.success("Marked as pending"); }
      else { await HomeworkAPI.markDone(hw.id, null); toast.success("Marked as done"); }
      onMarkChanged?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  return (
    <div
      onClick={onOpen}
      className="border border-border rounded-lg bg-card p-4 hover:shadow-md hover:border-primary/40 transition cursor-pointer group"
      data-testid={`hw-card-${hw.id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-[15px] truncate">{hw.title}</h3>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            {className && <Badge variant="outline" className="text-[10px] font-normal">{className}</Badge>}
            {hw.section_id && <Badge variant="outline" className="text-[10px] font-normal">Sec ·</Badge>}
            <DueBadge dueDate={hw.due_date} />
          </div>
        </div>
        {isTeacher && (isAuthor || (hw.created_by || "").length === 0) && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition" onClick={(e) => e.stopPropagation()}>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); onEdit(); }} data-testid={`hw-edit-${hw.id}`}>
              <Pencil className="h-3 w-3" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={remove} disabled={busy} data-testid={`hw-del-${hw.id}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground line-clamp-2 min-h-[2rem]">{hw.body}</p>
      <div className="flex items-center justify-between mt-3 pt-2.5 border-t border-border">
        <div className="text-[11px] text-muted-foreground flex items-center gap-2.5 flex-wrap">
          <span>by {hw.created_by_name || "—"}</span>
          {attCount > 0 && <span className="flex items-center gap-0.5"><Paperclip className="h-3 w-3" />{attCount}</span>}
          {hw.link_url && <LinkIcon className="h-3 w-3" />}
          {isTeacher && hw.submission_count > 0 && (
            <span className="text-emerald-700 font-semibold" data-testid={`hw-subs-${hw.id}`}>
              <CheckCircle2 className="h-3 w-3 inline mr-0.5" />{hw.submission_count} submitted
            </span>
          )}
        </div>
        {isStudent && (
          <Button size="sm" variant={hw.my_status === "done" ? "outline" : "default"} onClick={toggleDone} disabled={busy} className="h-7 text-xs" data-testid={`hw-mark-${hw.id}`}>
            {hw.my_status === "done" ? <><CheckCircle2 className="h-3 w-3 mr-1" />Done</> : "Mark done"}
          </Button>
        )}
      </div>
    </div>
  );
}

function EmptyState({ hasFilters }) {
  return (
    <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="hw-empty">
      <ClipboardList className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
      <div className="font-semibold">{hasFilters ? "No homework matches your filters" : "No homework yet"}</div>
      <div className="text-xs text-muted-foreground mt-1">
        {hasFilters ? "Try adjusting the filters or search term." : "Teachers can post the first assignment using \"New Homework\"."}
      </div>
    </div>
  );
}

function WeeklyCalendar({ items, onSelect }) {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1); // Monday of current week
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart); d.setDate(d.getDate() + i);
      return d;
    });
  }, [weekStart]);
  const byDay = useMemo(() => {
    const m = new Map();
    items.forEach((h) => {
      const key = h.due_date;
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(h);
    });
    return m;
  }, [items]);

  const shift = (delta) => {
    const d = new Date(weekStart); d.setDate(d.getDate() + delta * 7);
    setWeekStart(d);
  };
  const fmt = (d) => d.toISOString().slice(0, 10);
  const fmtLabel = (d) => d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });

  return (
    <div className="border border-border rounded-lg bg-card p-3" data-testid="hw-calendar">
      <div className="flex items-center justify-between mb-3">
        <Button variant="outline" size="sm" onClick={() => shift(-1)} data-testid="hw-cal-prev"><ChevronLeft className="h-3.5 w-3.5" /></Button>
        <div className="text-sm font-semibold">
          Week of {weekStart.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}
        </div>
        <Button variant="outline" size="sm" onClick={() => shift(1)} data-testid="hw-cal-next"><ChevronRight className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {days.map((d) => {
          const list = byDay.get(fmt(d)) || [];
          const isToday = fmt(d) === fmt(new Date());
          return (
            <div key={fmt(d)} className={`border rounded-md p-2 min-h-[110px] ${isToday ? "border-primary bg-primary/5" : "border-border"}`}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{fmtLabel(d)}</div>
              <div className="space-y-1 mt-1.5">
                {list.map((h) => (
                  <button key={h.id} onClick={() => onSelect(h)} className="w-full text-left text-[11px] px-1.5 py-1 rounded bg-white border border-border hover:border-primary truncate" data-testid={`hw-cal-item-${h.id}`}>
                    {h.title}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DetailDialog({ hw, isTeacher, className, onClose }) {
  const subsQ = useQuery({
    queryKey: ["hw-detail-subs", hw.id],
    queryFn: () => HomeworkAPI.submissions(hw.id),
    enabled: isTeacher,
  });
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="hw-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl">{hw.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            {className && <Badge variant="outline">{className}</Badge>}
            <DueBadge dueDate={hw.due_date} />
            <span className="text-xs text-muted-foreground">by {hw.created_by_name || "—"}</span>
          </div>
          <div className="text-sm whitespace-pre-wrap">{hw.body}</div>
          {hw.link_url && (
            <a href={hw.link_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
              <ExternalLink className="h-3.5 w-3.5" /> {hw.link_url}
            </a>
          )}
          {(hw.attachment_urls || []).length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">Attachments ({hw.attachment_urls.length})</div>
              <div className="space-y-1">
                {hw.attachment_urls.map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline">
                    <Paperclip className="h-3.5 w-3.5" /> Attachment {i + 1}
                  </a>
                ))}
              </div>
            </div>
          )}
          {isTeacher && (
            <div className="pt-2 border-t">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground mb-1.5">
                Submissions {subsQ.data ? `(${subsQ.data.length})` : ""}
              </div>
              {subsQ.isLoading ? <div className="py-4 text-center text-xs text-muted-foreground">Loading…</div>
                : (subsQ.data || []).length === 0 ? <div className="py-4 text-center text-xs text-muted-foreground">No submissions yet.</div>
                : (
                  <ul className="divide-y max-h-[240px] overflow-y-auto border rounded">
                    {subsQ.data.map((s) => (
                      <li key={s.id} className="px-3 py-2 text-sm">
                        <div className="font-medium">{s.student_name || s.student_id}</div>
                        {s.note && <div className="text-xs text-muted-foreground mt-0.5">{s.note}</div>}
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateEditDialog({ mode, hw, instId, classes, onClose, onSaved }) {
  const [title, setTitle] = useState(hw?.title || "");
  const [body, setBody] = useState(hw?.body || "");
  const [linkUrl, setLinkUrl] = useState(hw?.link_url || "");
  const [dueDate, setDueDate] = useState(hw?.due_date || new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10));
  const [classId, setClassId] = useState(hw?.class_id || "");
  const [sectionId, setSectionId] = useState(hw?.section_id || "");
  const [subjectId, setSubjectId] = useState(hw?.subject_id || "");
  const [attachmentUrls, setAttachmentUrls] = useState(hw?.attachment_urls || []);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const sectionsQ = useQuery({
    queryKey: ["hw-create-sections", classId],
    queryFn: () => SectionsAPI.list({ class_id: classId }),
    enabled: !!classId,
  });
  const subjectsQ = useQuery({
    queryKey: ["hw-create-subjects", classId],
    queryFn: () => SubjectsAPI.list({ class_id: classId }),
    enabled: !!classId,
  });

  const upload = async (files) => {
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const res = await HomeworkAPI.upload(fd);
        uploaded.push(res.url);
      }
      setAttachmentUrls((prev) => [...prev, ...uploaded]);
      toast.success(`Uploaded ${uploaded.length} file${uploaded.length === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "create") {
        await HomeworkAPI.create({
          institution_id: instId,
          class_id: classId,
          section_id: sectionId || null,
          subject_id: subjectId || null,
          title: title.trim(),
          body: body.trim(),
          link_url: linkUrl.trim() || null,
          attachment_urls: attachmentUrls,
          due_date: dueDate,
        });
        toast.success("Homework published");
      } else {
        await HomeworkAPI.update(hw.id, {
          title: title.trim(),
          body: body.trim(),
          link_url: linkUrl.trim() || null,
          due_date: dueDate,
          subject_id: subjectId || null,
          attachment_urls: attachmentUrls,
        });
        toast.success("Homework updated");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="hw-create-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? "Post new homework" : "Edit homework"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} placeholder="Mathematics: Trigonometry Ex. 8.1" data-testid="hw-form-title" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={body} onChange={(e) => setBody(e.target.value)} required maxLength={5000} rows={4} placeholder="Complete Ex. 8.1 Q1-15 from the NCERT textbook. Show all steps." data-testid="hw-form-body" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Class</Label>
              <Select value={classId} onValueChange={(v) => { setClassId(v); setSectionId(""); setSubjectId(""); }} disabled={mode === "edit"}>
                <SelectTrigger data-testid="hw-form-class"><SelectValue placeholder="Select class" /></SelectTrigger>
                <SelectContent>
                  {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Section (optional)</Label>
              <Select value={sectionId || "none"} onValueChange={(v) => setSectionId(v === "none" ? "" : v)} disabled={!classId || mode === "edit"}>
                <SelectTrigger data-testid="hw-form-section"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— All sections —</SelectItem>
                  {(sectionsQ.data || []).map((s) => <SelectItem key={s.id} value={s.id}>Section {s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Subject (optional)</Label>
              <Select value={subjectId || "none"} onValueChange={(v) => setSubjectId(v === "none" ? "" : v)} disabled={!classId}>
                <SelectTrigger data-testid="hw-form-subject"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {(subjectsQ.data || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required data-testid="hw-form-due" />
            </div>
            <div>
              <Label className="text-xs">Reference link (optional)</Label>
              <Input type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://…" data-testid="hw-form-link" />
            </div>
          </div>
          <div>
            <Label className="text-xs flex items-center gap-1.5">
              <Paperclip className="h-3 w-3" /> Attachments ({attachmentUrls.length})
            </Label>
            <div className="mt-1.5 flex items-center gap-2 flex-wrap">
              <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 border border-border rounded-md bg-secondary hover:bg-secondary/80 text-xs" data-testid="hw-form-upload-btn">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                {uploading ? "Uploading…" : "Add file(s)"}
                <input type="file" className="hidden" multiple onChange={(e) => e.target.files.length && upload(Array.from(e.target.files))} disabled={uploading} />
              </label>
              {attachmentUrls.map((u, i) => (
                <div key={i} className="inline-flex items-center gap-1 px-2 py-1 border border-border rounded text-xs bg-muted/40">
                  <Paperclip className="h-3 w-3" />
                  <a href={u} target="_blank" rel="noopener noreferrer" className="hover:underline max-w-[120px] truncate">File {i + 1}</a>
                  <button type="button" onClick={() => setAttachmentUrls((p) => p.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-destructive">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy || uploading} data-testid="hw-form-submit">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              {mode === "create" ? "Publish homework" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
